import { requestAccountDeletion } from "api/auth";
import { useDialogFocus } from "hooks/useDialogFocus";
import React, { type ChangeEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "Session";

import type { DeleteAccountDialogProps } from "../settings.types";

import styles from "./DeleteAccountDialog.module.css";

/**
 * Confirmation dialog for deleting the account (GDPR Art. 17, TTAPI-47). The account is deactivated at
 * once and erased after 30 days; signing back in or contacting support before then restores it. The
 * user types a confirmation word first, because unlike deactivation this ends in permanent erasure.
 */
const DeleteAccountDialog = ({ onClose }: DeleteAccountDialogProps) => {
	const { t } = useTranslation("settingsPage");
	const { getAccessToken, signOut } = useSession();
	const dialogRef = useDialogFocus<HTMLDivElement>();

	const [confirmation, setConfirmation] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");

	const confirmWord = t("deleteAccount.confirmWord");
	const isConfirmed = confirmation.trim().toUpperCase() === confirmWord.toUpperCase();

	const onConfirmationChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>): void => setConfirmation(event.target.value),
		[]
	);

	const onConfirm = useCallback(async (): Promise<void> => {
		if (isSubmitting || !isConfirmed) {
			return;
		}

		setErrorMessage("");
		setIsSubmitting(true);
		try {
			const token = await getAccessToken();
			if (!token) {
				return;
			}

			await requestAccountDeletion(token);
			signOut();
		} catch (error) {
			setErrorMessage(error instanceof Error ? error.message : t("deleteAccount.genericError"));
			setIsSubmitting(false);
		}
	}, [getAccessToken, isConfirmed, isSubmitting, signOut, t]);

	return (
		<div className={styles.overlay}>
			<div
				ref={dialogRef}
				aria-labelledby="delete-account-title"
				aria-modal="true"
				className={styles.dialog}
				role="alertdialog">
				<header className={styles.header}>
					<h2 id="delete-account-title">{t("deleteAccount.title")}</h2>
					<p>{t("deleteAccount.description")}</p>
				</header>

				<ul className={styles.consequences}>
					<li>{t("deleteAccount.consequenceNow")}</li>
					<li>{t("deleteAccount.consequenceKept")}</li>
					<li>{t("deleteAccount.consequenceRestore")}</li>
					<li>{t("deleteAccount.consequenceRetained")}</li>
					<li className={styles.finalWarning}>{t("deleteAccount.consequenceFinal")}</li>
				</ul>

				<label className={styles.confirmField} htmlFor="delete-account-confirm">
					<span>{t("deleteAccount.confirmPrompt", { word: confirmWord })}</span>
					<input
						autoComplete="off"
						className={styles.confirmInput}
						disabled={isSubmitting}
						id="delete-account-confirm"
						onChange={onConfirmationChange}
						type="text"
						value={confirmation}
					/>
				</label>

				{errorMessage && <p className={styles.error}>{errorMessage}</p>}

				<footer className={styles.footer}>
					<button className={styles.cancelButton} disabled={isSubmitting} onClick={onClose} type="button">
						{t("deleteAccount.cancel")}
					</button>
					<button
						className={styles.dangerButton}
						disabled={isSubmitting || !isConfirmed}
						onClick={onConfirm}
						type="button">
						{isSubmitting ? t("deleteAccount.deleting") : t("deleteAccount.confirm")}
					</button>
				</footer>
			</div>
		</div>
	);
};

export default DeleteAccountDialog;
